"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { useSlider } from "@/hooks/use-slider";

// Reusable Phone Component
type PhoneProps = {
  imageSrc?: string;
  alt?: string;
  scale?: number;
  onClick?: () => void;
  className?: string;
  priority?: boolean;
  children?: React.ReactNode;
};

function Phone({
  imageSrc,
  alt,
  scale = 1,
  onClick,
  className = "",
  priority = false,
  children,
}: PhoneProps) {
  const baseWidth = 18; // rem
  const baseHeight = 37.5; // rem

  return (
    <button
      type="button"
      className={`bg-transparent border-none p-0 ${onClick ? "cursor-pointer" : ""} ${className}`}
      onClick={onClick}
      style={{
        width: `${baseWidth * scale}rem`,
        height: `${baseHeight * scale}rem`,
        transform: scale !== 1 ? `scale(${scale})` : undefined,
      }}
      disabled={!onClick}
    >
      <div className="w-full h-full bg-black rounded-[3rem] p-[0.5rem] shadow-2xl">
        {/* iPhone Screen */}
        <div className="relative w-full h-full bg-black rounded-[2.5rem] overflow-hidden">
          {/* Notch */}
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[40%] h-[1.5rem] bg-black rounded-b-2xl z-10"></div>

          {/* Screen Content */}
          <div className="relative w-full h-full rounded-[2.5rem] overflow-hidden">
            {children ? (
              children
            ) : imageSrc ? (
              <Image
                src={imageSrc}
                alt={alt || "Phone screen"}
                fill
                sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                className="object-contain"
                priority={priority}
              />
            ) : null}
          </div>
        </div>

        {/* Home Indicator */}
        <div className="absolute bottom-[0.5rem] left-1/2 transform -translate-x-1/2 w-[40%] h-[0.25rem] bg-white rounded-full opacity-60"></div>
      </div>
    </button>
  );
}

export function PhoneSlider() {
  const t = useTranslations("phoneSlider");

  // App screens with actual images from assets
  const appScreens = [
    {
      id: 1,
      title: t("screens.scan.title"),
      description: t("screens.scan.description"),
      image: "/scanner.jpg",
      alt: t("screens.scan.alt"),
    },
    {
      id: 2,
      title: t("screens.aiRecognition.title"),
      description: t("screens.aiRecognition.description"),
      image: "/recognize.jpg",
      alt: t("screens.aiRecognition.alt"),
    },
    {
      id: 3,
      title: t("screens.nfcVerification.title"),
      description: t("screens.nfcVerification.description"),
      image: "/nfc-verify.jpg",
      alt: t("screens.nfcVerification.alt"),
    },
    {
      id: 4,
      title: t("screens.collection.title"),
      description: t("screens.collection.description"),
      image: "/collection.jpg",
      alt: t("screens.collection.alt"),
    },
  ];

  // Use the custom slider hook
  const {
    currentIndex: currentScreen,
    setIsHovered,
    handleNext,
    handlePrevious,
    handleSlideClick,
  } = useSlider(appScreens.length);

  // Helper functions to get previous and next screen indices
  const getPreviousScreenIndex = () => {
    return currentScreen === 0 ? appScreens.length - 1 : currentScreen - 1;
  };

  const getNextScreenIndex = () => {
    return (currentScreen + 1) % appScreens.length;
  };

  return (
    <div className="relative w-full">
      {/* Phone Container - needs to be wider to accommodate side phones */}
      <div className="relative w-full max-w-5xl h-[37.5rem] mx-auto">
        {/* Left Phone (Previous Slide) */}
        <Phone
          imageSrc={appScreens[getPreviousScreenIndex()].image}
          alt={appScreens[getPreviousScreenIndex()].alt}
          scale={0.8}
          onClick={handlePrevious}
          className="absolute left-0 top-[10%] z-10 opacity-60 hover:opacity-80 transition-opacity duration-200"
        />

        {/* Right Phone (Next Slide) */}
        <Phone
          imageSrc={appScreens[getNextScreenIndex()].image}
          alt={appScreens[getNextScreenIndex()].alt}
          scale={0.8}
          onClick={handleNext}
          className="absolute right-0 top-[10%] z-10 opacity-60 hover:opacity-80 transition-opacity duration-200"
        />

        {/* Main iPhone Frame with Slider */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: Hover functionality for pausing slider */}
        <div
          className="absolute left-1/2 top-0 transform -translate-x-1/2 z-30"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Phone className="relative">
            {/* Screen Content with Slider */}
            {appScreens.map((screen, index) => (
              <div
                key={screen.id}
                className={`absolute inset-0 transition-transform duration-500 ease-in-out ${
                  index === currentScreen
                    ? "transform translate-x-0"
                    : index < currentScreen
                      ? "transform -translate-x-full"
                      : "transform translate-x-full"
                }`}
              >
                <Image
                  src={screen.image}
                  alt={screen.alt}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  className="object-contain"
                  priority={index === currentScreen}
                />
              </div>
            ))}
          </Phone>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="flex justify-center mt-[2rem] space-x-[0.5rem]">
        {appScreens.map((screen, index) => (
          <button
            key={screen.id}
            type="button"
            onClick={() => handleSlideClick(index)}
            className={`w-[0.75rem] h-[0.75rem] rounded-full transition-colors duration-300 ${
              index === currentScreen ? "bg-black" : "bg-gray-300"
            }`}
            aria-label={`Go to slide ${index + 1}: ${screen.title}`}
          />
        ))}
      </div>

      {/* Screen Title */}
      <div className="text-center mt-[1.5rem]">
        <h4 className="text-xl font-semibold text-gray-800 mb-[0.5rem]">
          {appScreens[currentScreen].title}
        </h4>
        <p className="text-gray-600 ">
          {appScreens[currentScreen].description}
        </p>
      </div>
    </div>
  );
}
